import { useState, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sanitizeFileName } from './utils'
import { documentsApiEvents, documentsApiSelectors } from 'modules/api/documents'
import { IDocument } from 'modules/api/documents/types'
import { apiStateSelectors } from 'modules/api/apiState'

interface Document extends IDocument {
  isEditing?: boolean
  tags?: Record<string, string>
}

export const useDocumentInfo = (className: string, classId: string, isReadOnly: boolean) => {
  const dispatch = useDispatch()

  // Get documents and tags
  const documentsByClass = useSelector((state: any) =>
    documentsApiSelectors.getDocumentsByClass(state)(className, classId),
  )
  const documentTags = useSelector(documentsApiSelectors.getAllDocumentTags)
  const lastRemovedDocumentId = useSelector(documentsApiSelectors.getLastRemovedDocumentId)

  // Get errors from centralized apiState
  const readFileListError = useSelector((state: any) =>
    apiStateSelectors.getErrorsByEvent(state)(documentsApiEvents.documents_request({ className, classId })),
  )
  const renameError = useSelector((state: any) =>
    apiStateSelectors.getErrorsByEvent(state)(documentsApiEvents.renameDocument_request),
  )
  const updateTagsError = useSelector((state: any) =>
    apiStateSelectors.getErrorsByEvent(state)(documentsApiEvents.updateDocumentTags_request),
  )
  const onSaveError = renameError || updateTagsError
  const fetchDocumentTagsError = useSelector((state: any) =>
    apiStateSelectors.getErrorsByEvent(state)(documentsApiEvents.documentTags_request),
  )

  const [removing, setRemoving] = useState<string[]>([])
  const [localDocumentEdits, setLocalDocumentEdits] = useState<Record<string, Partial<Document>>>({})

  // Merge documents with local edits for UI
  const documents = useMemo(() => {
    return documentsByClass.map(doc => ({
      ...doc,
      ...localDocumentEdits[doc.id],
    }))
  }, [documentsByClass, localDocumentEdits])

  // Main refresh function to fetch documents
  const refreshDocuments = useCallback(() => {
    if (!classId) return

    // Dispatch the request action - epic will handle the API call
    dispatch(documentsApiEvents.documents_request({ className, classId }))
  }, [classId, className, dispatch])

  // Fetch tags for all documents when documents change
  useEffect(() => {
    if (documentsByClass.length > 0) {
      documentsByClass.forEach(doc => {
        // Only fetch if we don't already have tags for this document
        if (!documentTags[doc.id]) {
          dispatch(documentsApiEvents.documentTags_request({ documentId: doc.id }))
        }
      })
    }
  }, [documentsByClass, documentTags, dispatch])

  // Download document function
  const downloadDocument = useCallback(
    (document: Document) => {
      dispatch(documentsApiEvents.downloadDocument_request({ documentId: document.id }))
    },
    [dispatch],
  )

  // Remove document function
  const onRemove = useCallback(
    (documentId: string) => {
      if (isReadOnly) {
        return
      }

      setRemoving(prev => [...prev, documentId])
      dispatch(
        documentsApiEvents.removeDocument_request({
          documentId,
          className,
          classId,
        }),
      )
    },
    [isReadOnly, className, classId, dispatch],
  )

  // Clear removing state when document is successfully removed
  useEffect(() => {
    if (lastRemovedDocumentId) {
      setRemoving(prev => prev.filter(id => id !== lastRemovedDocumentId))
      dispatch(documentsApiEvents.clearLastRemovedDocumentId())
    }
  }, [lastRemovedDocumentId, dispatch])

  // Handle document name change for inline editing
  const onNameChange = useCallback((file: Document, event: React.ChangeEvent<HTMLInputElement>) => {
    const rawNewName = event.target.value
    const sanitizedName = sanitizeFileName(rawNewName)

    // Show warning if the name was modified during sanitization
    if (rawNewName !== sanitizedName && rawNewName.length > 0) {
      console.warn('File name was sanitized:', { original: rawNewName, sanitized: sanitizedName })
    }

    // Update the document in local edits state with sanitized name
    setLocalDocumentEdits(prev => ({
      ...prev,
      [file.id]: { ...prev[file.id], name: sanitizedName, isEditing: true },
    }))
  }, [])

  // Handle edit mode - set a document to editing
  const onEdit = useCallback((file: Document) => {
    // Clear all other documents' isEditing state and set this one to editing
    setLocalDocumentEdits(prev => {
      const updated: Record<string, Partial<Document>> = {}
      // Clear isEditing from all documents
      Object.keys(prev).forEach(id => {
        if (prev[id].isEditing) {
          updated[id] = { ...prev[id], isEditing: false }
        }
      })
      // Set this document to editing
      updated[file.id] = { ...prev[file.id], isEditing: true }
      return { ...prev, ...updated }
    })
  }, [])

  // Handle document tags change for inline editing
  const onTagsChange = useCallback((file: Document, event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value

    // Create tags object with the selected tag
    const newTagsObject: Record<string, string> = {}
    if (selectedValue && selectedValue !== '') {
      newTagsObject[selectedValue] = selectedValue
    }

    // Update the document in local edits state with new tags
    setLocalDocumentEdits(prev => ({
      ...prev,
      [file.id]: { ...prev[file.id], tags: newTagsObject, isEditing: true },
    }))
  }, [])

  // Handle save rename and tags
  const onSave = useCallback(
    (file: Document) => {
      if (isReadOnly) {
        return
      }

      const localEdits = localDocumentEdits[file.id]

      // Only dispatch rename if the name was actually edited
      if (localEdits?.name !== undefined) {
        dispatch(
          documentsApiEvents.renameDocument_request({
            documentId: file.id,
            name: file.name,
            className,
            classId,
          }),
        )
      }

      // Only dispatch tags update if the tags were actually edited
      if (localEdits?.tags !== undefined) {
        dispatch(
          documentsApiEvents.updateDocumentTags_request({
            documentId: file.id,
            tags: localEdits.tags,
          }),
        )
      }

      // Clear local edit state
      setLocalDocumentEdits(prev => {
        const updated = { ...prev }
        delete updated[file.id]
        return updated
      })
    },
    [isReadOnly, className, classId, documentTags, localDocumentEdits, dispatch],
  )

  // Return all the state and functions that the component needs
  return {
    documents,
    removing,
    documentTags: documentTags,
    readFileListError,
    onSaveError,
    fetchDocumentTagsError,
    refreshDocuments,
    downloadDocument,
    onRemove,
    onEdit,
    onNameChange,
    onTagsChange,
    onSave,
  }
}
